/**
 * StyledTextField Styles & Animations
 *
 * Theme-aware styled components and animations for the StyledTextField component.
 * All styles use theme tokens exclusively - NO hardcoded values.
 */

import { TextField, Box } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import type { AnimatedTextFieldProps } from './StyledTextField.types';

// ============================================================================
// ANIMATIONS
// ============================================================================

/**
 * Glow animation for field updates
 * Indicates when a field has been updated externally
 */
export const fieldUpdateGlow = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(25, 118, 210, 0.2);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0);
  }
`;

/**
 * Background color animation for value changes
 * Briefly highlights when field value changes
 */
export const fieldValueChange = keyframes`
  0% {
    background-color: rgba(25, 118, 210, 0.08);
  }
  100% {
    background-color: transparent;
  }
`;

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

/**
 * Animated TextField with update indicators
 *
 * Features:
 * - Smooth transitions for all state changes
 * - Glow animation for external updates
 * - Color-coded borders based on update source
 * - Theme-aware styling throughout
 */
export const AnimatedTextField = styled(TextField, {
  shouldForwardProp: (prop) =>
    prop !== 'hasRecentUpdate' && prop !== 'updateSource'
})<AnimatedTextFieldProps>(
  ({ theme, hasRecentUpdate, updateSource }) => ({
    position: 'relative',

    // Input container styling
    '& .MuiOutlinedInput-root': {
      transition: theme.transitions.create(['box-shadow', 'border-color', 'background-color'], {
        duration: theme.transitions.duration.standard,
      }),

      // Browser autofill override - transparent background
      '& input:-webkit-autofill': {
        WebkitBoxShadow: `0 0 0 1000px transparent inset !important`,
        WebkitTextFillColor: `${theme.palette.text.primary} !important`,
        transition: 'background-color 5000s ease-in-out 0s',
      },

      // Update animation and border color
      ...(hasRecentUpdate && {
        animation: `${fieldUpdateGlow} 2s ease-out`,
        '& fieldset': {
          borderColor: updateSource === 'firebase'
            ? theme.palette.info.main
            : updateSource === 'system'
            ? theme.palette.warning.main
            : updateSource === 'ai'
            ? theme.palette.secondary.main
            : theme.palette.success.main,
        },
      }),
    },

    // Input text styling
    '& .MuiInputBase-input': {
      // Value change animation
      ...(hasRecentUpdate && {
        animation: `${fieldValueChange} 1.5s ease-out`,
      }),
    },

    // Label styling
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
      '&.Mui-focused': {
        color: theme.palette.primary.main,
      },
      '&.Mui-error': {
        color: theme.palette.error.main,
      },
    },

    // Helper text styling
    '& .MuiFormHelperText-root': {
      marginLeft: 0,
      color: theme.palette.text.secondary,
      '&.Mui-error': {
        color: theme.palette.error.main,
      },
    },

    // Adornment styling
    '& .MuiInputAdornment-root': {
      color: theme.palette.text.secondary,

      // Icon button styling within adornments
      '& .MuiIconButton-root': {
        padding: theme.spacing(0.5),
        color: theme.palette.text.secondary,
        transition: theme.transitions.create(['color', 'background-color'], {
          duration: theme.transitions.duration.shorter,
        }),

        '&:hover': {
          backgroundColor: theme.palette.action.hover,
          color: theme.palette.text.primary,
        },

        '&:focus': {
          backgroundColor: theme.palette.action.focus,
        },
      },
    },
  })
);

/**
 * Wrapper for update indicator
 * Positions the indicator in the top-right corner of the field
 */
export const UpdateIndicatorWrapper = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  zIndex: 1,
  pointerEvents: 'none', // Allow clicks to pass through to the field
}));

// ============================================================================
// THEME-AWARE UTILITIES
// ============================================================================

/**
 * Get field styles based on state and theme
 */
export const getFieldStyles = (theme: any, hasError: boolean, hasRecentUpdate: boolean) => ({
  // Error state styling
  ...(hasError && {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: theme.palette.error.main,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.error.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.error.main,
        borderWidth: 2,
      },
    },
  }),

  // Recent update styling
  ...(hasRecentUpdate && {
    '& .MuiOutlinedInput-root': {
      '&:not(.Mui-error)': {
        '& fieldset': {
          borderWidth: 2,
        },
      },
    },
  }),
});

/**
 * Get update source color
 */
export const getUpdateSourceColor = (theme: any, source: string) => {
  switch (source) {
    case 'firebase':
      return theme.palette.info.main;
    case 'system':
      return theme.palette.warning.main;
    case 'ai':
      return theme.palette.secondary.main;
    case 'user':
    default:
      return theme.palette.success.main;
  }
};